<template xmlns="http://www.w3.org/1999/html">
    <form @submit.prevent="$bvModal.show(`refundRequest-confirm-approve-${edit}-${refundRequest.id}`)">
        <b-card class="dispatchRequest mb-4 mt-1 font-size-md"
                no-body
                :class="{
                    'opacity-7': history && refundRequest.is_replaced,
                    'opacity-2': isChanged,
                }"
                :data-refund-request-id="refundRequest.id"
        >
            <b-card-header header-bg-variant="light">
                <b-col cols="12" xl="3" class="px-0">
                    <h5 class="mb-0 d-inline align-bottom">
                        <span :title="refundRequest.status"><refund-request-icon :status="refundRequest.status" class="mr-2"/></span>
                    </h5>
                    <h6 class="mb-0 d-inline">
                        <strong>{{ refundRequest.status | refundRequestStatus | uppercase }}</strong>
                        <small v-if="refundRequest.status === 'proposal'" class="d-inline-block pl-2">
                            (Leveringsstatus: {{ refundRequest.dispatch_request.status | dispatchRequestStatus }})
                        </small>
                    </h6>
                </b-col>
                <b-col cols="12" xl="3">
                    <h5 class="mb-0" v-if="!edit">
                        <small>
                            <strong>Gemaakt op: </strong>
                            <span>{{ refundRequest.created_at | datetimeshort }}</span>
                        </small>
                    </h5>
                </b-col>
                <b-col cols="12" xl="3">
                    <h5 class="mb-0">
                        <small class="ml-2">
                            <span v-if="refundAmountExceedsBudget(editableRefundRequest)">
                                <FontAwesomeIcon icon="warning" class="text-danger mr-2" title="Het bedrag van creditering is hoger dan het beschikbare budget" />
                            </span>
                            <span v-if="showBudget(editableRefundRequest)">
                                <strong>Budget: </strong>
                                <span> {{this.refundRequest.total_refundable_amount | price }} </span>
                            </span>
                            <span v-else>
                                <strong>Crediteren: </strong>
                                <span v-if="isNaN(totalRefundAmount)" class="text-danger">Foute invoer</span>
                                <span v-else>{{ totalRefundAmount | price }}</span>
                            </span>
                        </small>
                    </h5>
                </b-col>
                <b-col :cols="(history) ? 3 : 2">
                    <h5 class="mb-0" :class="{blur: shouldBlur}">
                        <small v-if="refundRequest.status === 'decision-denied-final'" class="text-danger">
                            <strong>Terugbetaald: </strong>
                            <span>0,00</span>
                        </small>
                        <small v-else-if="refundRequest.status === 'refunded'" class="text-success">
                            <strong>Terugbetaald: </strong>
                            <span>{{ totalRefundAmount | price }}</span>
                        </small>
                    </h5>
                </b-col>
                <b-col v-if="!edit && !history" cols="12" xl="1" class="text-center text-lg-right pr-0 mt-3 mt-lg-0">
                    <b-button variant="outline-secondary" size="sm" class="ml-2" title="Bekijk geschiedenis"
                              @click="$bvModal.show(`showRefundRequestHistory-${refundRequest.id}`)"
                              :disabled="isSaving"
                    >
                        <font-awesome-icon icon="eye"/>
                    </b-button>
                    <span v-if="isEditable && refundRequest.status === 'proposal'">
                        <b-button variant="outline-secondary" size="sm" class="ml-2" title="Wijzigen"
                                  @click="$bvModal.show(`editRefundRequest_${refundRequest.id}`)"
                                  :disabled="isSaving"
                        >
                            <font-awesome-icon icon="edit"/>
                        </b-button>
                    </span>
                </b-col>
            </b-card-header>
            <b-alert v-if="refundRequest.metadata && refundRequest.metadata.release_time" show variant="secondary" class="mb-0">
                Deze creditering wordt automatisch verwerkt op: {{ refundRequest.metadata.release_time | datetime }}
            </b-alert>
            <b-alert v-if="!refundRequest.refund_method_id" show variant="warning" class="mb-0">
                <strong>Let op:</strong> Deze creditering heeft geen terugbetalingsmethode ingesteld. Neem contact op met OmniStock.
            </b-alert>
            <b-alert v-if="refundRequest.metadata && !!refundRequest.metadata.caused_by_trigger_id" show variant="info" class="mb-0">
                <font-awesome-icon v-if="!trigger" icon="spinner" spin/>
                <span v-else>
                    <strong>Let op:</strong> De actie <i>"{{ trigger.actions[0].action_component_display_name }}"</i> is uitgevoerd omdat de creditering voldoet aan deze controles:

                    <ul class="m-0 mt-2 pl-4">
                        <li v-for="condition in relevantTriggerConditions">
                            {{ condition.condition_class_display_name }} {{ condition.operator }} {{ condition.value }}
                        </li>
                    </ul>
                </span>
            </b-alert>

            <b-card-body class="pl-4 pb-3 pt-4" body-bg-variant="default"
                         :class="{blur: shouldBlur}"
            >

                <b-table-simple v-if="refundRequest.refund_request_lines" small borderless responsive>
                    <colgroup>
                        <col style="width:10%;"/>
                        <col style="width:40%;"/>
                        <col style="width:10%;"/>
                        <col style="width:10%;"/>
                        <col style="width:10%;"/>
                        <col style="width:10%;"/>
                        <col style="width:10%;"/>
                    </colgroup>
                    <b-thead>
                        <b-tr>
                            <b-th>Artikel nr</b-th>
                            <b-th>Productnaam</b-th>
                            <b-th>Aantal</b-th>
                            <b-th>Stuksprijs</b-th>
                            <b-th>Regelprijs</b-th>
                            <b-th>Terugbetaling</b-th>
                            <b-th v-if="showLineBudget(refundRequest)">Beschikbaar Budget</b-th>
                            <b-th v-else></b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="line in flattenedLines" :data-refund-request-line-id="line.refundLine?.id" :data-refund-request-line-addition-id="line.refundLineAddition?.id">
                            <b-td class="align-middle">{{ line.product_identifier }}</b-td>
                            <b-td class="align-middle">{{ line.product_name }}</b-td>
                            <b-td class="align-middle">{{ line.quantity }}</b-td>
                            <b-td class="align-middle">{{ line.piece_price | price }}</b-td>
                            <b-td class="align-middle">{{ line.line_price | price }}</b-td>
                            <b-td class="align-middle">
                                <span v-if="line.refundLineAddition && !edit">{{ line.refundLineAddition.refund_amount | price }}</span>
                                <span v-else-if="!line.refundLineAddition && !edit">{{ line.refundLine.refund_amount | price }}</span>
                                <b-input v-else-if="line.refundLineAddition" size="sm" type="number" v-model.trim="line.refundLineAddition.refund_amount" min="0" :max="line.total_refundable_amount" step="any"
                                         :class="{
                                            'border-warning': refundLineAdditionAmountExceedsBudget(line)
                                         }"
                                         :data-line-gross-price="line.refundLine.dispatch_request_line?.sales_order_line?.line_gross_price"
                                         class="d-inline-block"
                                         style="width: calc(100% - 5px);"
                                />
                                <b-input v-else size="sm" type="number" v-model.trim="line.refundLine.refund_amount" min="0" :max="line.total_refundable_amount" step="any"
                                         :class="{
                                            'border-warning': line.refundLine.refund_amount > line.refundLine.dispatch_request_line?.sales_order_line?.line_gross_price || line.refundLine.refund_amount < 0
                                         }"
                                         :data-line-gross-price="line.refundLine.dispatch_request_line?.sales_order_line?.line_gross_price"
                                         class="d-inline-block"
                                         style="width: calc(100% - 5px);"
                                />
                            </b-td>
                            <b-td class="align-middle" v-if="showLineBudget(refundRequest)">
                                <span v-if="line.refundLineAddition">
                                    <FontAwesomeIcon v-if="refundLineAdditionAmountExceedsBudget(line)" icon="warning" class="text-danger mr-1" title="Het bedrag van de regel is hoger dan het beschikbare budget" />
                                    <FontAwesomeIcon v-if="line.refundLineAddition.refund_amount === ''" icon="warning" class="text-danger mr-1" title="Ongeldige invoer" />
                                    {{ (line.refundLineAddition.total_refundable_amount) | price }}
                                </span>
                                <span v-else>
                                    <FontAwesomeIcon v-if="refundLineAmountExceedsBudget(editableRefundRequest, line)" icon="warning" class="text-danger mr-1" title="Het bedrag van de regel is hoger dan het beschikbare budget" />
                                    <FontAwesomeIcon v-if="line.refundLine.refund_amount === ''" icon="warning" class="text-danger mr-1" title="Ongeldige invoer" />
                                    {{ (line.total_refundable_amount) | price}}
                                </span>
                            </b-td>
                            <b-td v-else></b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>

                <b-table-simple v-if="editableRefundRequest.refund_request_additions && editableRefundRequest.refund_request_additions.length" small borderless>
                    <colgroup>
                        <col style="width:80%;"/>
                        <col style="width:10%;"/>
                        <col style="width:10%;"/>
                    </colgroup>
                    <b-thead>
                        <b-tr>
                            <b-th>Toevoeging</b-th>
                            <b-th>Terugbetaling</b-th>
                            <b-th></b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="refundRequestAddition in editableRefundRequest.refund_request_additions">
                            <b-td class="align-middle">{{ refundRequestAddition.reason }}</b-td>
                            <b-td class="align-middle">
                                <span v-if="!edit">{{ refundRequestAddition.amount | price }}</span>
                                <b-input v-else size="sm" type="number" v-model.trim="refundRequestAddition.amount" min="0" step="any"
                                         :class="{
                                            'border-danger': refundRequestAddition.refund_amount < 0
                                         }"
                                         class="d-inline-block w-75"/>
                            </b-td>
                            <b-td class="align-middle" v-if="showLineBudget(refundRequest)">
                                <FontAwesomeIcon v-if="refundAdditionAmountExceedsBudget(refundRequestAddition)" icon="warning" class="text-danger mr-1" title="Het bedrag van de regel is hoger dan het beschikbare budget" />
                                <FontAwesomeIcon v-if="refundRequestAddition.amount === ''" icon="warning" class="text-danger ml-2" title="Ongeldige invoer" />
                                {{ refundRequestAddition.total_refundable_amount | price}}
                            </b-td>
                            <b-td v-else></b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>

                <b-table-simple small borderless>
                    <colgroup>
                        <col style="width:80%;"/>
                        <col style="width:20%;"/>
                    </colgroup>
                    <b-thead>
                        <b-tr>
                            <b-th>Totaal</b-th>
                            <b-th>
                                Terugbetaling
                                <span class="font-weight-normal">(via: {{ refundRequest.refund_method?.display_name }})</span>
                            </b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr>
                            <b-td>Totaal</b-td>
                            <b-td>
                                <span v-if="refundAmountExceedsBudget(refundRequest)">
                                    <FontAwesomeIcon icon="warning" class="text-danger mr-2" title="Het bedrag van creditering is hoger dan het beschikbare budget" />
                                </span>
                                <span v-if="isNaN(totalRefundAmount)" class="text-danger">Foute invoer</span>
                                <span v-else>{{ totalRefundAmount | price }}</span>
                            </b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>
            </b-card-body>

            <b-card-footer footer-bg-variant="light" footer-border-variant="light" :class="{'pb-0': !isEditable}">
                <b-col cols="10" class="pr-4 pl-0 text-black-50">
                    <div class="d-flex flex-row" v-if="!shouldBlur">
                        <div class="pr-2">
                            <strong v-if="isEditable.comment || canComment">Opmerking:</strong>
                            <span v-if="!canComment"> {{ refundRequest.comment }}</span>
                        </div>
                        <div class="flex-fill">
                            <b-textarea v-if="canComment" size="sm" v-model="editableRefundRequest.comment" style="height:70px;"
                                        :required="$store.state.config.refunds['require-comment']"
                                        :class="{
                                            'border-danger': $store.state.config.refunds['require-comment'] && !editableRefundRequest.comment
                                        }"
                            />
                        </div>
                    </div>
                    <h5 v-else-if="refundRequest.status === 'refund-to-be-processed-manually' && !history" class="text-info mb-0">
                        Vergrendel deze creditering om details te zien
                    </h5>
                    <h5 v-else-if="refundRequest.status === 'refund-processing-manually' && !isRefundRequestFromCurrentUser" class="text-info mb-0">
                        Deze handmatige uitbetaling wordt al door {{ refundRequest.user_id ? refundRequest.user.name : 'systeem' }} uitgevoerd
                    </h5>
                </b-col>
                <b-col v-if="isEditable" cols="2" class="text-right pr-0">
                    <span v-if="refundRequest.status === 'proposal'">
                        <b-button type="submit" variant="success" size="lg" class="float-right ml-2"
                                  :disabled="!isApprovable(editableRefundRequest) || isSaving"
                                  :title="(!this.edit) ? `Goedkeuren` : `Opslaan en goedkeuren`"
                        >
                            <font-awesome-icon icon="thumbs-up"/>
                        </b-button>
                        <b-button v-if="isDenyable" type="button" variant="danger" size="lg" class="float-right ml-2" title="Weigeren"
                                  @click="$bvModal.show(`refundRequest-confirm-deny-${edit}-${refundRequest.id}`)"
                                  :disabled="!isDenyable || isSaving"
                        >
                            <font-awesome-icon icon="ban"/>
                        </b-button>
                    </span>
                    <span v-if="refundRequest.status === 'refund-to-be-processed-manually'">
                        <b-button type="button" variant="outline-primary" size="lg" class="float-right ml-2" title="Vergrendel"
                                  @click="lock()"
                                  :disabled="isSaving"
                        >
                            <font-awesome-icon icon="lock-open"/>
                        </b-button>
                    </span>
                    <span v-if="refundRequest.status === 'refund-processing-manually' && isRefundRequestFromCurrentUser">
                        <b-button type="button" variant="success" size="lg" class="float-right ml-2" title="Verwerkt"
                                  @click="$bvModal.show(`refundRequest-confirm-confirm-refund-to-be-processed-manually-${edit}-${refundRequest.id}`)"
                                  :disabled="isSaving"
                        >
                            <font-awesome-icon icon="check"/>
                        </b-button>
                        <b-button type="button" variant="primary" size="lg" class="float-right ml-2" title="Ontgrendel"
                                  @click="unlock()"
                                  :disabled="isSaving"
                        >
                            <font-awesome-icon icon="lock"/>
                        </b-button>
                    </span>
                    <span v-if="refundRequest.status === 'proposal-to-be-released-automatically'">
                        <b-button type="button" variant="outline-success" size="md" class="float-right ml-2" title="Nu verwerken"
                                  @click="$bvModal.show(`refundRequest-confirm-release-automatic-processing-${edit}-${refundRequest.id}`)"
                                  :disabled="isSaving"
                        >
                            <font-awesome-icon icon="thumbs-up"/>
                        </b-button>
                        <b-button type="button" variant="outline-info" size="md" class="float-right ml-2" title="Zet terug naar 'te accorderen'"
                                  @click="$bvModal.show(`refundRequest-confirm-cancel-automatic-processing-${edit}-${refundRequest.id}`)"
                                  :disabled="isSaving"
                        >
                            <font-awesome-icon icon="undo"/>
                        </b-button>
                    </span>
                </b-col>
            </b-card-footer>
            <b-card-footer v-if="!isEditable" footer-bg-variant="light" footer-border-variant="light" class="pt-0">
                <b-col cols="12" class="pr-0 text-black-50 pl-0">
                    <small><strong>Gewijzigd door:</strong> {{ refundRequest.user_id ? refundRequest.user.name : 'systeem' }}</small>
                </b-col>
            </b-card-footer>
        </b-card>

        <b-modal v-if="!edit" :id="`editRefundRequest_${refundRequest.id}`" size="xl"
                 title="Creditering accorderen"
                 centered
                 dialog-class="shadow-none"
                 footer-class="d-none"
        >
            <refund-request :refund-request="refundRequest" :edit="true"
                            @close="$bvModal.hide(`editRefundRequest_${refundRequest.id}`)"
                            @changed="$emit('changed')"
            />
        </b-modal>
        <b-modal v-if="!edit" :id="`showRefundRequestHistory-${refundRequest.id}`" size="xl"
                 title="Creditering's geschiedenis"
                 centered
                 dialog-class="shadow-none"
                 footer-class="d-none"
                 @show="loadHistory"
        >
            <font-awesome-icon v-if="isLoadingHistory" icon="spinner" spin/>
            <refund-request v-for="historicRefundRequest in refundRequestHistorySorted"
                            :refund-request="historicRefundRequest" :edit="false" :history="true"
                            @close="$bvModal.hide(`editRefundRequest_${refundRequest.id}`)"
                            @changed="$emit('changed')"
            />
        </b-modal>
        <b-modal :id="`refundRequest-confirm-approve-${edit}-${refundRequest.id}`"
                 title="Goedkeuren"
                 size="md"
                 centered
                 dialog-class="shadow-none"
                 @ok="approve"
        >
            Weet je zeker dat je {{ totalRefundAmount | price }} wilt terugbetalen?
        </b-modal>
        <b-modal :id="`refundRequest-confirm-deny-${edit}-${refundRequest.id}`"
                 title="Weigeren"
                 size="md"
                 centered
                 dialog-class="shadow-none"
                 @ok="deny"
        >
            Weet je zeker dat je deze creditering wilt weigeren?
        </b-modal>
        <b-modal :id="`refundRequest-confirm-confirm-refund-to-be-processed-manually-${edit}-${refundRequest.id}`"
                 title="Handmatige uitbetaling"
                 size="md"
                 centered
                 dialog-class="shadow-none"
                 @ok="confirmProcessed"
        >
            Weet je zeker dat je de handmatig uitbetaling op verwerkt wilt zetten?
        </b-modal>
        <b-modal :id="`refundRequest-confirm-cancel-automatic-processing-${edit}-${refundRequest.id}`"
                 title="Annuleer automatische uitbetaling"
                 size="md"
                 centered
                 dialog-class="shadow-none"
                 @ok="confirmCancelAutomaticProcessing"
        >
            Weet je zeker dat je de automatische uitbetaling wilt annuleren?<br/>
            De uitbetaling zal worden omgezet naar 'te accorderen'.
        </b-modal>
        <b-modal :id="`refundRequest-confirm-release-automatic-processing-${edit}-${refundRequest.id}`"
                 title="Nu verwerken"
                 size="md"
                 centered
                 dialog-class="shadow-none"
                 @ok="confirmReleaseAutomaticProcessing"
        >
            Weet je zeker dat je deze uitbetaling nu wilt laten verwerken?
        </b-modal>
    </form>
</template>

<script>
import RefundRequestIcon from "../../../components/RefundRequestIcon";
import {STATUSES_THAT_REQUIRE_ACTION} from "../Refunds"
import _ from "lodash";

export default {
    name: "RefundRequest",
    components: {RefundRequestIcon},

    props: {
        refundRequest: {required: true, type: Object},
        edit: {required: false, default: false, type: Boolean},
        history: {required: false, default: false, type: Boolean},
    },

    data() {
        return {
            isSaving: false,
            isLoadingHistory: false,
            isChanged: false,
            actionApprove: false,
            actionDeny: false,
            editableRefundRequest: {},
            refundRequestHistory: [],
            trigger: null,
            flattenedLines: [],
        }
    },

    computed: {
        isEditable() {
            return STATUSES_THAT_REQUIRE_ACTION.indexOf(this.refundRequest.status) !== -1
                && !this.history
                && this.hasPermission('refunds.approve')
        },
        canComment() {
            return this.isEditable
                && this.refundRequest.status !== 'proposal-to-be-released-automatically'
        },
        totalRefundAmount() {
            if (this.edit) {
                // When in edit mode calculate the refund amount on the fly so we can check that it doesn't exceed the max. refundable amount
                return parseFloat(_.sumBy(this.editableRefundRequest.refund_request_lines, line => parseFloat(line.refund_amount)))
                    + parseFloat(_.sumBy(this.editableRefundRequest.refund_request_additions, line => parseFloat(line.amount)))
                    + parseFloat(_.sumBy(
                        this.editableRefundRequest.refund_request_lines,
                        line => _.sumBy(line.refund_request_line_additions, 'refund_amount')),
                    )
            }
            return this.editableRefundRequest.total_refund_amount
        },
        isDenyable() {
            return !this.edit
                && (
                    !this.$store.state.config.refunds['require-comment']
                    || (this.$store.state.config.refunds['require-comment'] && this.editableRefundRequest.comment)
                )
        },
        refundRequestHistorySorted() {
            return _.orderBy(this.refundRequestHistory, ['created_at', 'id'], ['desc', 'desc'])
        },
        shouldBlur() {
            return !this.history
                && (
                    this.refundRequest.status === 'refund-to-be-processed-manually'
                    || (
                        this.refundRequest.status === 'refund-processing-manually'
                        && !this.isRefundRequestFromCurrentUser
                    )
                )
        },
        isRefundRequestFromCurrentUser() {
            return this.refundRequest.user_id === this.$store.state.user.id
        },
        relevantTriggerConditions() {
            if (!this.refundRequest.metadata) {
                return []
            }

            return this.trigger.conditions.filter(c => c.group_identifier === this.refundRequest.metadata.caused_by_trigger_condition_group)
        },
        isFromShippedWithIssuesDrq() {
            return this.refundRequest.dispatch_request?.status === 'shipped-with-issues'
        },
    },

    mounted() {
        this.loadCausedByTrigger()

        this.editableRefundRequest = _.cloneDeep(this.refundRequest)
        this.editableRefundRequest.comment = ''
        this.flattenedLines = this.flattenRefundLinesAndAdditions(this.editableRefundRequest.refund_request_lines)


        _.each(this.editableRefundRequest.refund_request_lines, line => {
            if (this.edit) {
                if (line.refund_amount > line.total_refundable_amount) {
                    line.refund_amount = line.total_refundable_amount;
                }
            }
            line.refund_amount = Math.round(line.refund_amount * 100) / 100
        })
        _.each(this.editableRefundRequest.refund_request_additions, line => {
            line.amount = Math.round(line.amount * 100) / 100
        })
    },

    methods: {
        isApprovable(refundRequest) {
            return this.totalRefundAmount > 0
                && (
                    !this.$store.state.config.refunds['require-comment']
                    || (this.$store.state.config.refunds['require-comment'] && this.editableRefundRequest.comment)
                )
                && !this.refundAmountExceedsBudget(refundRequest)
                && !this.flattenedLines.some(line => {
                    return (line.refundLineAddition)
                        ? this.refundLineAdditionAmountExceedsBudget(line)
                        : this.refundLineAmountExceedsBudget(refundRequest, line)
                })
                && !refundRequest.refund_request_additions?.some(addition => this.refundAdditionAmountExceedsBudget(addition))
        },
        showLineBudget(refundRequest) {
            return refundRequest.status === 'proposal';
        },
        showBudget(refundRequest) {
            return refundRequest.status === 'proposal' && this.refundAmountExceedsBudget(refundRequest);
        },
        refundAmountExceedsBudget(refundRequest) {
            if (!refundRequest.id || refundRequest.status !== 'proposal') {
                return false;
            }

            let refundable = Math.floor(refundRequest.total_refundable_amount * 100);
            let refund = Math.floor(this.totalRefundAmount * 100);

            return refundable < (refund - 1);
        },
        refundAdditionAmountExceedsBudget(refundRequestAddition) {
            let refundable = Math.floor(refundRequestAddition.total_refundable_amount * 100);
            let refund = Math.floor(refundRequestAddition.amount * 100);

            return refundable < (refund - 1);
        },
        refundLineAmountExceedsBudget(refundRequest, line) {
            let refundable = Math.floor(line.refundLine.total_refundable_amount * 100);
            let refund = Math.floor(line.refundLine.refund_amount * 100);

            return refundable < (refund - 1);
        },
        refundLineAdditionAmountExceedsBudget(line) {
            return line.refundLineAddition?.refund_amount > line.refundLineAddition?.total_refundable_amount || line.refundLineAddition?.refund_amount < 0;
        },
        loadCausedByTrigger() {
            if (!this.refundRequest.metadata?.caused_by_trigger_id) {
                return
            }

            this.$http.get(`/api/triggers/${this.refundRequest.metadata.caused_by_trigger_id}`, {
                params: {
                    include: 'conditions;actions',
                },
            })
                .then(response => {
                    this.trigger = response.data
                })
                .catch(e => {
                    this.$alert(`Trigger not found`, 'danger')
                })
        },
        loadHistory() {
            if (this.refundRequestHistory.length) {
                return
            }

            const relations = [
                'refund_method:id,display_name',
                'refund_request_lines.dispatch_request_line.sales_order_line',
                'refund_request_additions',
                'dispatch_request.dispatch_request_lines',
            ]

            this.isLoadingHistory = true
            this.$http.get(`/api/rest/refund-requests/${this.refundRequest.id}/history?include=${relations.join(';')}`)
                .then(response => {
                    this.refundRequestHistory = response.data
                })
                .catch(e => {
                    this.$alert("Fout bij ophalen van de geschiedenis", 'danger', true)
                })
                .then(() => {
                    this.isLoadingHistory = false
                })
        },
        save(endPoint, postData) {
            this.isSaving = true

            return this.$http.put(endPoint, postData)
                .then(response => {
                    this.isChanged = true;
                    this.$emit('changed')
                })
                .catch(e => {
                    console.error(e)
                    this.$alert("Er is iets misgegaan bij het opslaan", 'danger')
                    this.isSaving = false
                })
        },
        approve() {
            this.save(
                `/api/rest/refund-requests/${this.refundRequest.id}/approve`,
                {
                    comment: this.editableRefundRequest.comment,
                    refund_lines: this.editableRefundRequest.refund_request_lines.map(line => {
                        return {
                            dispatch_request_line_id: line.dispatch_request_line.id,
                            refund_amount: line.refund_amount,
                            refund_line_additions: line.refund_request_line_additions.map(lineAddition => ({
                                refund_amount: lineAddition.refund_amount,
                                reason: lineAddition.reason,
                            }))
                        }
                    }),
                    refund_additions: this.editableRefundRequest.refund_request_additions.map(line => {
                        return {
                            reason: line.reason,
                            amount: line.amount,
                        }
                    })
                }
            )
        },
        deny() {
            this.save(
                `/api/rest/refund-requests/${this.refundRequest.id}/deny`,
                {
                    comment: this.editableRefundRequest.comment
                }
            )
        },
        lock() {
            this.save(
                `/api/rest/refund-requests/${this.refundRequest.id}/lock-manual-processing`,
                {}
            )
        },
        unlock() {
            this.save(
                `/api/rest/refund-requests/${this.refundRequest.id}/unlock-manual-processing`,
                {}
            )
        },
        confirmProcessed() {
            this.save(
                `/api/rest/refund-requests/${this.refundRequest.id}/finish-manual-processing`,
                {
                    comment: this.editableRefundRequest.comment
                }
            )
        },
        confirmCancelAutomaticProcessing() {
            this.save(`/api/rest/refund-requests/${this.refundRequest.id}/cancel-automatic-processing`)
        },
        confirmReleaseAutomaticProcessing() {
            this.save(`/api/rest/refund-requests/${this.refundRequest.id}/release-automatic-processing`)
        },
        flattenRefundLinesAndAdditions(refundLines) {
            return _.chain(refundLines)
                .map(refundLine => {
                    let lines = [{
                        id: refundLine.id,
                        product_identifier: refundLine.dispatch_request_line.sales_order_line.product_identifier,
                        product_name: refundLine.dispatch_request_line.sales_order_line.product_name,
                        quantity: refundLine.quantity,
                        piece_price: refundLine.dispatch_request_line.sales_order_line.line_gross_price / refundLine.dispatch_request_line.sales_order_line.quantity,
                        line_price: refundLine.dispatch_request_line.sales_order_line.line_gross_price,
                        refund_amount: refundLine.refund_amount,
                        total_refundable_amount: refundLine.total_refundable_amount,
                        refundLine: refundLine,
                        refundLineAddition: null,
                    }]

                    refundLine.refund_request_line_additions?.forEach(lineAddition => {

                        lineAddition.original_refund_amount = Math.round(lineAddition.refund_amount * 100) / 100
                        lineAddition.refund_amount = Math.round(lineAddition.refund_amount * 100) / 100

                        const reasonTranslateKey = `refunds.line-addition-reasons.${lineAddition.reason}`
                        let translatedReason = this.$t(reasonTranslateKey).toString()
                        if (translatedReason === reasonTranslateKey) {
                           translatedReason = lineAddition.reason
                        }

                        if (this.isFromShippedWithIssuesDrq) {
                            lineAddition.refund_amount = 0
                        }

                        lines.push({
                            id: `${refundLine.id}_${lineAddition.id}`,
                            product_identifier: '',
                            product_name: ` + ${translatedReason}`,
                            quantity: '',
                            piece_price: '',
                            line_price: lineAddition.refund_amount,
                            refund_amount: '',
                            total_refundable_amount: '',
                            refundLine: refundLine,
                            refundLineAddition: lineAddition,
                        })
                    })

                    return lines
                })
                .flatten(1)
                .value()
        },
    },
}
</script>

<style scoped lang="scss">
.blur {
    filter: blur(4px);
}
</style>
